import {
  ApolloCache,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  Company,
  Country,
  ErrorResponse,
  FundingStatus,
  GET_COMPANY,
  GET_COUNTRIES,
  PATCH_COMPANY,
  UPSERT_FUNDING_STATUS,
} from '@frontend/data-access';
import { employeeNumberOptions } from '@frontend/shared/ui';
import {
  App,
  Button,
  DatePicker,
  Form,
  Radio,
  Select,
  Space,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

interface FormValues {
  location?: string;
  foundedDate?: Date;
  employeeNumber?: string;
  bankEngagement?: string;
}

function EditBasicsForm({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');

  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const { data: dataCountries, loading: loadingCountries } = useQuery<{
    countries: Country[];
  }>(GET_COUNTRIES);
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [form] = Form.useForm();

  const bankEngagements = useMemo(
    () => [
      {
        value: '1',
        label: t('bank_engagements_no'),
      },
      {
        value: '2',
        label: t('bank_engagements_discussion'),
      },
      {
        value: '3',
        label: t('bank_engagements_poc_phase'),
      },
      {
        value: '4',
        label: t('bank_engagements_production'),
      },
      {
        value: '5',
        label: t('bank_engagements_invested'),
      },
    ],
    [t]
  );

  const handleCompleted = () => {
    notification.success({
      message: t('successfully_saved'),
    });
    onFinish();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: data.message,
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onError: handleError,
  });

  const handleUpdateCache = (
    cache: ApolloCache<NormalizedCacheObject>,
    result: { data?: { upsertFundingStatus: FundingStatus | null } | null }
  ) => {
    if (result.data?.upsertFundingStatus) {
      const cacheData = cache.readQuery<{ company: Company }>({
        query: GET_COMPANY,
        variables: { id: companyId },
      });

      if (cacheData?.company) {
        cache.writeQuery({
          query: GET_COMPANY,
          data: {
            company: {
              ...cacheData.company,
              progressDetails: {
                ...cacheData.company.progressDetails,
                funding:
                  !!result.data.upsertFundingStatus.bankEngagement &&
                  typeof result.data.upsertFundingStatus
                    .currentlyFundraising === 'boolean',
              },
              fundingStatus: result.data.upsertFundingStatus,
            },
          },
        });
      }
    }
  };

  const [upsertFundingStatus, { loading: loadingUpsertFundingStatus }] =
    useMutation(UPSERT_FUNDING_STATUS, {
      onError: handleError,
      update: handleUpdateCache,
    });

  const handleOnFinish = async (values: FormValues) => {
    const patchData = await patchCompany({
      variables: {
        input: {
          id: companyId,
          foundedDate: values.foundedDate
            ? new Date(values.foundedDate)
            : undefined,
          companyInfo: {
            location: values.location,
            employeeNumber: values.employeeNumber,
          },
        },
      },
    });
    const upsertData = await upsertFundingStatus({
      variables: {
        upsertFundingStatusInput: {
          companyId,
          bankEngagement: values.bankEngagement,
        },
      },
    });

    if (!patchData.errors && !upsertData.errors) {
      handleCompleted();
    }
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  useEffect(() => {
    if (data?.company) {
      form.setFieldsValue({
        location: data.company.companyInfo?.location,
        foundedDate: data.company.foundedDate
          ? dayjs(data.company.foundedDate)
          : null,
        employeeNumber: data.company.companyInfo?.employeeNumber,
        bankEngagement: data.company.fundingStatus?.bankEngagement,
      });
    }
  }, [data, form]);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark="optional"
      onFinish={handleOnFinish}
      onFieldsChange={handleFormChange}
    >
      <Form.Item name="location" label={t('hq_location')}>
        <Select
          placeholder={t('enter_country')}
          loading={loadingCountries}
          showSearch
          filterOption={(input, option) =>
            ((option?.label as string) ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={dataCountries?.countries.map((country) => ({
            value: country.name,
            label: country.name,
          }))}
          data-test="country-select"
        />
      </Form.Item>
      <Form.Item name="foundedDate" label={t('founded_year')}>
        <DatePicker
          placeholder={t('select_date')}
          data-test="founded-date-input"
        />
      </Form.Item>
      <Form.Item label={t('number_of_employees')} name="employeeNumber">
        <Select
          placeholder={t('select_number_of_employees')}
          options={employeeNumberOptions.map((option) => ({
            label: option,
            value: option,
          }))}
          data-test="employee-count-input"
        />
      </Form.Item>
      <Form.Item
        label={t('engagement_with_bank')}
        name="bankEngagement"
        data-test="engagement-input"
      >
        <Radio.Group>
          <Space direction="vertical">
            {bankEngagements.map((item) => {
              return (
                <Radio key={item.value} value={item.value}>
                  {item.label}
                </Radio>
              );
            })}
          </Space>
        </Radio.Group>
      </Form.Item>
      <div className="flex justify-end items-center gap-2">
        <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          loading={loadingPatch || loadingUpsertFundingStatus}
          disabled={isIncomplete}
          shape="round"
        >
          {t('save')}
        </Button>
      </div>
    </Form>
  );
}

export default EditBasicsForm;
